import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { TooltipArrow } from "@radix-ui/react-tooltip";
import { CloseIcon, InfoPlayerIcon } from "@roman/shared/components/Icons";
import type { Player } from "@roman/shared/types/Player";
import { cn } from "@roman/shared/utils";
import { useState } from "react";
import { PlayerDetailButton } from "./components/PlayerDetailButton";

type IPlayerCard = {
  player: Player;
  currentIndex: number;
  isSelected: boolean;
  selectedPlayers: string[];
  setCurrentIndex?: (index: number) => void;
  setSelectedPlayers?: (fn: (draft: string[]) => void) => void;
  onClick?: (draft: string[], index: number) => void;
  roundId: string;
};

export const PlayerCard = ({
  player,
  currentIndex,
  selectedPlayers,
  setCurrentIndex = () => {},
  setSelectedPlayers = () => {},
  isSelected,
  roundId,
}: IPlayerCard) => {
  const [isClicked, setIsClicked] = useState(false);
  const [isOpenTooltip, setIsOpenTooltip] = useState<boolean>(false);

  const handleClick = () => {
    if (isOpenTooltip) {
      return;
    }
    setIsClicked(true);
    handleClickSelectPlayer();
    setTimeout(() => {
      setIsClicked(false);
    }, 150);
  };

  const handleClickSelectPlayer = () => {
    setSelectedPlayers((draft) => {
      draft[currentIndex] = player.id;
    });

    if (currentIndex < selectedPlayers.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  return (
    <div
      onClick={handleClick}
      className={cn(
        "bg-white cursor-pointer p-1.5 rounded-lg flex flex-col items-center drop-shadow-2xl max-w-[165px]",
        (isSelected || isClicked) && "shadow-[0px_0px_35px_10px_#01DCF9]",
      )}
    >
      <img
        src={`${player?.imageUrl || "/assets/player-1.png"}`}
        alt="player-1"
        className="rounded-md w-[152px] h-[228px]"
        draggable={false}
      />

      <div className="relative mt-2 w-full h-[93px] flex flex-col justify-between items-start">
        <div className="text-xs font-bold">{player.name}</div>

        <PlayerDetailButton id={player.id} selectedPlayers={selectedPlayers} roundId={roundId} />

        <div className="flex flex-col gap-1 items-center pt-2 mt-2 absolute bottom-0 right-0 z-50">
          <div
            className="px-2"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpenTooltip(true);
            }}
          >
            <div
              className="text-white flex flex-col items-center justify-center h-[40px] w-[43px] "
              style={{
                background: "linear-gradient(280deg, #176CF2 14.15%, #02A3F9 79.87%)",
                clipPath: "polygon( 50% 0, 100% 38%, 81% 100%, 19% 100%, 0 38%)",
              }}
            >
              <span className="font-bold text-[14px] leading-[17px] pt-[5px] w-full text-center">
                {player.averageFantasyPoint.toFixed(1)}
              </span>
              <span className="text-[8px] leading-[8px] w-full text-center">pts</span>
            </div>
          </div>

          <div className="text-center flex justify-center items-center gap-[2px]">
            <p className="font-bold text-[10px] text-primary">活躍ポイント</p>
            <TooltipProvider>
              <Tooltip
                delayDuration={0}
                open={isOpenTooltip}
                defaultOpen={isOpenTooltip}
                onOpenChange={setIsOpenTooltip}
              >
                <TooltipTrigger>
                  <InfoPlayerIcon
                    className="w-[12px] h-[12px]"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsOpenTooltip(true);
                    }}
                  />
                </TooltipTrigger>
                <TooltipContent
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsOpenTooltip(true);
                  }}
                  align="end"
                  className="bg-black text-white w-[240px] -mr-[20px] p-0 border-0 z-50"
                >
                  <div className="relative w-full h-full">
                    <div className="pl-[16px] text-[10px] pr-[27px] py-[10px] font-medium">
                      <div className="font-bold">活躍ポイント=FP（Fantasy Point）とは?</div>
                      選手の直近8試合でのパフォーマンスに基づいて計算されるポイントです。
                    </div>
                    <div
                      className="absolute top-1 right-1 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsOpenTooltip(false);
                      }}
                    >
                      <CloseIcon />
                    </div>
                  </div>
                  <TooltipArrow display={0} className="fill-black w-[14px] h-[14px] -mt-[1px]" />
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>
    </div>
  );
};
